import { h, Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { Text } from 'preact-i18n';
import {
    MapContainer, TileLayer, Marker, Popup, Polyline, Circle, useMap, useMapEvents,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import {
    formatDateNo, mapBounds, groupByPosition, fanOutOffsets, colorForType, hasCoordinates, championshipFor, selectRowHandler,
} from '../../lib/nsfCompetitionUi';

// Map of mirrored NSF competitions. Every marker gets an explicit divIcon, so the
// Leaflet default-icon URL patch is not needed (same reasoning as components/map/editor.js).
// Stevner on the same position collapse into one counted marker; a click fans them out
// in a circle (pixel offsets from fanOutOffsets), a map click or zoom collapses again.
const NORWAY_CENTER = [64.5, 12.5];
const FIT_OPTIONS = { padding: [30, 30], maxZoom: 12 };
const TILE_URL = 'https://cache.kartverket.no/v1/wmts/1.0.0/topograatone/default/webmercator/{z}/{y}/{x}.png';
const HOME_COLOR = '#0d6efd';
const CLUSTER_COLOR = '#343a40';

/** Small dot at the centre of the «Nær meg» radius when no home pin sits there. */
function centerIcon() {
    return L.divIcon({
        html: `<div class="nsf-pin-center" style="background:${HOME_COLOR};width:12px;height:12px;border-radius:50%;`
            + 'border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>',
        className: 'nsf-competition-marker',
        iconSize: [12, 12],
        iconAnchor: [6, 6],
    });
}

/** Four points on the edge of the radius so the map fit includes the whole circle. */
function radiusExtent(radius) {
    const dLat = radius.km / 111;
    const dLng = radius.km / (111 * Math.max(0.2, Math.cos((radius.lat * Math.PI) / 180)));
    return [
        { lat: radius.lat + dLat, lng: radius.lng },
        { lat: radius.lat - dLat, lng: radius.lng },
        { lat: radius.lat, lng: radius.lng + dLng },
        { lat: radius.lat, lng: radius.lng - dLng },
    ];
}

function validRadius(radius) {
    return !!radius && Number.isFinite(radius.lat) && Number.isFinite(radius.lng) && Number.isFinite(radius.km) && radius.km > 0;
}

function pinIcon({
    color, cancelled = false, home = false, count = 0,
}) {
    const size = count > 1 ? 28 : 22;
    let background = color;
    let glyph = '●';
    if (home) {
        background = HOME_COLOR;
        glyph = '⌂';
    } else if (count > 1) {
        background = CLUSTER_COLOR;
        glyph = String(count);
    }
    return L.divIcon({
        html: `<div style="background:${background};color:#fff;width:${size}px;height:${size}px;border-radius:50%;display:flex;`
            + `align-items:center;justify-content:center;font-size:${count > 1 ? 13 : 12}px;font-weight:600;`
            + `box-shadow:0 1px 4px rgba(0,0,0,.4);opacity:${cancelled ? 0.6 : 1}">${glyph}</div>`,
        className: 'nsf-competition-marker',
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -size / 2],
    });
}

function RowPopup({ row, onSelect }) {
    const cancelled = row.status >= 2;
    return (
        <Popup>
            <div class='small'>
                <strong>{row.title}</strong>
                {championshipFor(row.competitionTypeName) && (
                    <span class='badge text-bg-warning ms-1'><i class='fa-duotone fa-trophy me-1' />{championshipFor(row.competitionTypeName)}</span>
                )}
                {cancelled && <span class='badge text-bg-danger ms-1'><Text id='terminliste.cancelled'>Cancelled</Text></span>}
                <div>{formatDateNo(row.startDate)}</div>
                <div>{row.organizationName}</div>
                <div class='text-body-secondary'>{row.competitionTypeName}</div>
                <a
                    href={`/terminliste/${row.id}`}
                    onClick={selectRowHandler(onSelect, row)}
                >
                    <Text id='terminliste.open'>Open</Text>
                </a>
            </div>
        </Popup>
    );
}

// Rendered inside MapContainer: collapses the fan-out on a map click or zoom.
/** Leaflet reads bounds/center only on mount: re-fit when the rows (and so the bounds) change. */
function FitTo({ bounds }) {
    const map = useMap();
    const key = bounds ? JSON.stringify(bounds) : '';
    useEffect(() => {
        if (!map) {
            return;
        }
        if (key) {
            map.fitBounds(JSON.parse(key), FIT_OPTIONS);
        } else {
            map.setView(NORWAY_CENTER, 5);
        }
    }, [map, key]);
    return null;
}

function CollapseOn({ onCollapse }) {
    useMapEvents({
        click() { onCollapse(); },
        zoomstart() { onCollapse(); },
    });
    return null;
}

// Fanned-out markers for one position group, placed by pixel offset from the centre.
function FanOut({ group, onSelect }) {
    const map = useMap();
    if (!map) {
        return null;
    }
    const center = map.latLngToContainerPoint([group.lat, group.lng]);
    const offsets = fanOutOffsets(group.rows.length);
    return (
        <>
            {group.rows.map((row, i) => {
                const point = L.point(center.x + offsets[i].dx, center.y + offsets[i].dy);
                const target = map.containerPointToLatLng(point);
                const cancelled = row.status >= 2;
                return (
                    <Fragment key={row.id}>
                        <Polyline positions={[[group.lat, group.lng], [target.lat, target.lng]]} pathOptions={{ color: '#999', weight: 1, opacity: 0.6 }} />
                        <Marker position={[target.lat, target.lng]} icon={pinIcon({ color: colorForType(row.competitionTypeName), cancelled })}>
                            <RowPopup row={row} onSelect={onSelect} />
                        </Marker>
                    </Fragment>
                );
            })}
        </>
    );
}

function Legend({ rows }) {
    const names = [];
    rows.forEach((row) => {
        const name = row.competitionTypeName || '';
        if (name && !names.includes(name)) {
            names.push(name);
        }
    });
    if (names.length === 0) {
        return null;
    }
    return (
        <div class='d-flex flex-wrap gap-2 small text-body-secondary mt-1'>
            {names.map(name => (
                <span key={name} class='text-nowrap'>
                    <span class='d-inline-block rounded-circle me-1 align-middle' style={`width:.65rem;height:.65rem;background:${colorForType(name)}`} />
                    {name}
                </span>
            ))}
        </div>
    );
}

// One-finger drag on phones would capture the page scroll: pan is off there, two-finger pinch still zooms.
const touchDevice = !!(L.Browser && L.Browser.mobile);

function NsfCompetitionMap({
    rows = [], home = null, radius = null, height = '60vh', onSelect, showLegend = true,
}) {
    const [fanned, setFanned] = useState(null);
    const groups = groupByPosition(rows.filter(hasCoordinates));
    const circle = validRadius(radius) ? radius : null;
    const homeAtCenter = !!(circle && home && hasCoordinates(home) && home.lat === circle.lat && home.lng === circle.lng);
    const bounds = mapBounds([...groups, ...(home ? [home] : []), ...(circle ? radiusExtent(circle) : [])]);
    const mapProps = bounds
        ? { bounds, boundsOptions: FIT_OPTIONS }
        : { center: NORWAY_CENTER, zoom: 5 };
    const fannedGroup = fanned ? groups.find(g => g.key === fanned) : null;
    return (
        <div>
            <div style={{ height, width: '100%' }} class='rounded overflow-hidden border'>
                <MapContainer
                    {...mapProps}
                    scrollWheelZoom={true}
                    dragging={!touchDevice}
                    style={{ height: '100%', width: '100%' }}
                >
                    <TileLayer
                        attribution='&copy; <a href="https://statkart.no">Kartverket</a>'
                        url={TILE_URL}
                    />
                    <CollapseOn onCollapse={() => setFanned(null)} />
                    <FitTo bounds={bounds} />
                    {groups.map((group) => {
                        if (group.rows.length > 1) {
                            if (fannedGroup && fannedGroup.key === group.key) {
                                return null;
                            }
                            return (
                                <Marker
                                    key={group.key}
                                    position={[group.lat, group.lng]}
                                    icon={pinIcon({ count: group.rows.length })}
                                    eventHandlers={{ click: () => setFanned(group.key) }}
                                />
                            );
                        }
                        const row = group.rows[0];
                        return (
                            <Marker
                                key={row.id}
                                position={[row.lat, row.lng]}
                                icon={pinIcon({ color: colorForType(row.competitionTypeName), cancelled: row.status >= 2 })}
                            >
                                <RowPopup row={row} onSelect={onSelect} />
                            </Marker>
                        );
                    })}
                    {fannedGroup && <FanOut group={fannedGroup} onSelect={onSelect} />}
                    {circle && (
                        <Circle
                            center={[circle.lat, circle.lng]}
                            radius={circle.km * 1000}
                            pathOptions={{
                                color: HOME_COLOR, weight: 2, dashArray: '6 6', fillColor: HOME_COLOR, fillOpacity: 0.05,
                            }}
                        />
                    )}
                    {circle && !homeAtCenter && <Marker position={[circle.lat, circle.lng]} icon={centerIcon()} />}
                    {home && hasCoordinates(home) && (
                        <Marker position={[home.lat, home.lng]} icon={pinIcon({ home: true })}>
                            <Popup><div class='small'>{home.label || <Text id='terminliste.homeLabelDefault'>Home</Text>}</div></Popup>
                        </Marker>
                    )}
                </MapContainer>
            </div>
            {showLegend && <Legend rows={rows} />}
        </div>
    );
}

export default NsfCompetitionMap;
